import { useEffect, useMemo, useState, useRef } from 'react';
import {
  Modal,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import linesOverlay from '../assets/images/Lines-no_background.png';
import MessageFormatter from './MessageFormatter';
import { useAuth } from '../contexts/AuthContext';

const LANGUAGE_FLAGS = {
  en: '🇬🇧',
  fi: '🇫🇮',
  sv: '🇸🇪'
};

const WELCOME_WORDS = [
  'Welcome',         // English (Ireland, Malta)
  'Tervetuloa',      // Finnish (Finland)
  'Välkommen',       // Swedish (Sweden)
  'Tere tulemast',   // Estonian (Estonia)
  'Willkommen',      // German (Germany, Austria)
  'Bienvenue',       // French (France, Belgium, Luxembourg)
  'Welkom',          // Dutch (Netherlands, Belgium)
  'Sveiki atvykę',   // Lithuanian (Lithuania)
  'Benvenuto',       // Italian (Italy)
  'Fáilte',          // Irish (Ireland)
  'Witamy',          // Polish (Poland)
  'Bienvenido',      // Spanish (Spain)
  'Bem-vindo',       // Portuguese (Portugal)
  'Vitajte',         // Slovak (Slovakia)
  'Καλώς ήρθατε',    // Greek (Greece, Cyprus)
  'Vítejte',         // Czech (Czech Republic)
  'Üdvözöljük',      // Hungarian (Hungary)
  'Bun venit',       // Romanian (Romania)
  'Dobrodošli',      // Croatian (Croatia)
  'Velkommen',       // Danish (Denmark)
  'Sveiki',          // Latvian (Latvia)
  'Merħba',          // Maltese (Malta)
  'Добре дошли',     // Bulgarian (Bulgaria)
  'Dobrodošli',      // Slovenian (Slovenia)
];

const containerStyles = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: ['100dvh', '100vh'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(245, 245, 245, 0.7)',
  backdropFilter: 'blur(3px)',
  zIndex: 2000
};

const overlayPaperStyles = {
  width: { xs: '92vw', md: '960px' },
  maxHeight: { xs: ['100dvh', '100vh'], md: '90vh' },
  borderRadius: 4,
  overflow: 'hidden',
  border: '1px solid rgba(0, 0, 0, 0.12)',
  display: 'flex',
  flexDirection: 'column'
};

const heroBoxStyles = {
  backgroundColor: '#021c2d',
  backgroundImage: `url(${linesOverlay})`,
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  color: 'white',
  padding: { xs: 2.25, md: 3.75 },
  position: 'relative',
  zIndex: 1
};

const formContainerStyles = {
  flex: 1,
  padding: { xs: 3, md: 5 },
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  overflowY: 'auto',
  backgroundColor: 'white'
};

const paragraphStyles = {
  fontSize: { xs: '0.95rem', md: '1rem' },
  lineHeight: 1.6,
  marginBottom: 2
};

const WelcomeOverlay = ({
  open,
  loading,
  data,
  onSubmit,
  onLanguageSelect,
  onGoToResults
}) => {
  const { isDemoMode } = useAuth();
  const isNewOrg = data?.is_new_org === true;
  const [title, setTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [responsibilities, setResponsibilities] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [language, setLanguage] = useState('');
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [welcomeWordIndex, setWelcomeWordIndex] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);

  const orgDepartments = useMemo(() => {
    const list = data?.organization_departments;
    if (!Array.isArray(list)) return [];
    return list.map((dept) => (typeof dept === 'string' ? dept.trim() : '')).filter(Boolean);
  }, [data?.organization_departments]);

  const titleRef = useRef(null);
  const responsibilitiesRef = useRef(null);
  const departmentRef = useRef(null);
  const submitButtonRef = useRef(null);

  useEffect(() => {
    if (data?.profile_defaults) {
      setTitle(data.profile_defaults.title || '');
      setDepartment(data.profile_defaults.department || '');
      setResponsibilities(data.profile_defaults.responsibilities || '');
    }
  }, [data]);

  // Autofocus logic: focus on first empty field, or submit button if all filled
  // Only run when overlay opens, not on every keystroke
  useEffect(() => {
    if (!open || loading) return;

    // Small delay to ensure form is rendered
    const timer = setTimeout(() => {
      const currentTitle = data?.profile_defaults?.title || '';
      const currentResponsibilities = data?.profile_defaults?.responsibilities || '';
      const currentDepartment = data?.profile_defaults?.department || '';

      if (!currentTitle) {
        titleRef.current?.focus();
      } else if (!currentResponsibilities) {
        responsibilitiesRef.current?.focus();
      } else if (!currentDepartment) {
        departmentRef.current?.focus();
      } else {
        submitButtonRef.current?.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [open, loading, data?.profile_defaults]);

  useEffect(() => {
    if (data?.languages) {
      const entries = Object.entries(data.languages);
      setAvailableLanguages(entries);
      const preferred = data.language && data.languages[data.language] ? data.language : entries[0]?.[0] || '';
      setLanguage(preferred);
    }
  }, [data?.languages, data?.language]);

  useEffect(() => {
    const interval = setInterval(() => {
      setFadeOut(true);
      setTimeout(() => {
        setWelcomeWordIndex((prev) => (prev + 1) % WELCOME_WORDS.length);
        setFadeOut(false);
      }, 2000);
    }, 7000);
    return () => clearInterval(interval);
  }, []);

  const introContent = useMemo(() => data?.intro_message?.trim() || '', [data?.intro_message]);

  const handleSubmit = async () => {
    setSubmitting(true);
    const selectedLanguage = language || data?.language || availableLanguages[0]?.[0] || '';
    const payload = {
      title: title.trim(),
      department: department.trim(),
      responsibilities: responsibilities.trim(),
      language: selectedLanguage
    };

    await onSubmit?.(payload);
    setSubmitting(false);
  };

  const handleLanguageSelect = (eventOrCode) => {
    const nextLanguage = typeof eventOrCode === 'string' ? eventOrCode : eventOrCode.target.value;
    if (nextLanguage === language) {
      return;
    }
    setLanguage(nextLanguage);
    onLanguageSelect?.(nextLanguage);
  };

  return (
    <Modal open={open}>
      <Box sx={containerStyles}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2
            }}
          >
            <CircularProgress size={48} sx={{ color: '#021c2d' }} />
            <Typography variant="subtitle1" sx={{ letterSpacing: 1, color: '#021c2d' }}>
              Initiating AI system...
            </Typography>
          </Box>
        ) : (
          <Paper elevation={0} sx={overlayPaperStyles}>
            <>
              <Box sx={{ ...heroBoxStyles, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ pr: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontFamily: 'Bebas Neue',
                        letterSpacing: 1
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          minWidth: '200px',
                          opacity: fadeOut ? 0 : 1,
                          transition: 'opacity 2s ease-in-out'
                        }}
                      >
                        {WELCOME_WORDS[welcomeWordIndex]}
                      </Box>
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={formContainerStyles}>
                <Stack spacing={3} sx={{ flex: 1 }}>
                  {introContent && (
                    <Box
                      sx={{
                        backgroundColor: '#f5f7fb',
                        borderRadius: 2,
                        p: 3,
                        border: '1px solid rgba(4, 40, 64, 0.08)'
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: 1,
                          mb: 1,
                          color: '#0d1b2a'
                        }}
                      >
                      </Typography>
                      <MessageFormatter content={introContent} />
                    </Box>
                  )}
                  {isNewOrg && (
                    <Box
                      sx={{
                        backgroundColor: '#e8f4f8',
                        borderRadius: 2,
                        p: 3,
                        border: '1px solid rgba(2, 28, 45, 0.15)'
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: 'Bebas Neue',
                          letterSpacing: 1,
                          mb: 2,
                          color: '#021c2d'
                        }}
                      >
                        New Organisation Created: {data?.organization_name || 'Your Organisation'}
                      </Typography>
                      <Box component="ul" sx={{
                        m: 0,
                        pl: 2.5,
                        '& li': {
                          fontSize: '0.95rem',
                          lineHeight: 1.4,
                          mb: 0.5,
                          color: '#1e3a4f'
                        }
                      }}>
                        <li>You are the first user to sign up from your organisation, it has been created automatically.</li>
                        <li><strong>You have been assigned as Organisation Manager.</strong></li>
                        <li>Other users from your organisation will automatically join as standard users (no access to organisation reports, user management or organisation settings).</li>
                        <li>As the Organisation Manager, you can at any time promote users to Org. Managers in Account → User Management.</li>
                        <li>Manage your organisation's detailed settings in Account → Organisation.</li>
                      </Box>
                    </Box>
                  )}
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1
                  }}>
                    <Typography variant="h5" sx={{ fontFamily: 'Bebas Neue', letterSpacing: 1 }}>
                      Describe Your Role
                    </Typography>
                    {availableLanguages.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 400,
                            color: '#6b7685'
                          }}
                        >
                          Interview language
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.75 }}>
                          {availableLanguages.map(([code, name]) => (
                            <Tooltip key={code} title={isDemoMode ? "Editing disabled in demo mode" : name} placement="top">
                              <Box
                                onClick={() => !isDemoMode && handleLanguageSelect(code)}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  cursor: isDemoMode ? 'not-allowed' : 'pointer',
                                  padding: '4px 8px',
                                  transition: 'transform 0.2s',
                                  opacity: isDemoMode ? 0.6 : 1,
                                  '&:hover': {
                                    transform: isDemoMode ? 'none' : 'scale(1.05)'
                                  }
                                }}
                              >
                                <Box sx={{
                                  fontSize: '1.4rem',
                                  opacity: 1
                                }}>
                                  {LANGUAGE_FLAGS[code] || '🌐'}
                                </Box>
                                <Box sx={{
                                  width: '100%',
                                  height: '2px',
                                  backgroundColor: language === code ? '#d1d5db' : 'transparent',
                                  borderRadius: '1px',
                                  marginTop: '2px',
                                  transition: 'background-color 0.2s'
                                }} />
                              </Box>
                            </Tooltip>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                  <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : ""} placement="top">
                    <TextField
                      label="Your Title"
                      placeholder="e.g. Head of Data & Analytics"
                      value={title}
                      onChange={(event) => setTitle(event.target.value)}
                      fullWidth
                      inputRef={titleRef}
                      disabled={isDemoMode}
                    />
                  </Tooltip>
                  <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : ""} placement="top">
                    <TextField
                      label="Key Responsibilities"
                      placeholder="Short summary of your core responsibilities. e.g. Leading data strategy and governance, Responsible for marketing camapigns."
                      value={responsibilities}
                      onChange={(event) => setResponsibilities(event.target.value)}
                      fullWidth
                      multiline
                      minRows={3}
                      inputRef={responsibilitiesRef}
                      disabled={isDemoMode}
                    />
                  </Tooltip>
                  {orgDepartments.length > 0 ? (
                    <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : ""} placement="top">
                      <FormControl fullWidth>
                        <InputLabel id="welcome-department-label">Department / Team</InputLabel>
                        <Select
                          labelId="welcome-department-label"
                          label="Department / Team"
                          value={department || ''}
                          onChange={(event) => setDepartment(event.target.value)}
                          inputRef={departmentRef}
                          disabled={isDemoMode}
                        >
                          <MenuItem value="">
                            <em>Select Department / Team</em>
                          </MenuItem>
                          {orgDepartments.map((dept, index) => (
                            <MenuItem key={index} value={dept}>
                              {dept}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Tooltip>
                  ) : (
                    <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : ""} placement="top">
                      <TextField
                        label="Department / Team"
                        placeholder="e.g. Sales, Development, HR, Strategy, Finance"
                        value={department}
                        onChange={(event) => setDepartment(event.target.value)}
                        fullWidth
                        inputRef={departmentRef}
                        disabled={isDemoMode}
                      />
                    </Tooltip>
                  )}
                  <Box sx={{
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: 'white',
                    zIndex: 1,
                    pt: 2,
                    mt: 'auto',
                    borderTop: { xs: '1px solid rgba(0, 0, 0, 0.08)', md: 'none' }
                  }}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between',
                      alignItems: { xs: 'stretch', sm: 'center' },
                      gap: 2
                    }}>
                      <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : ""} placement="top">
                        <span>
                          <Button
                            ref={submitButtonRef}
                            variant="contained"
                            size="large"
                            onClick={handleSubmit}
                            disabled={submitting || isDemoMode}
                            disableRipple={false}
                            fullWidth={true}
                            sx={{
                              backgroundColor: '#021c2d',
                              '&:hover': { backgroundColor: '#042a44' },
                              '&:focus': { outline: 'none', boxShadow: 'none' },
                              '&:focus-visible': { outline: 'none', boxShadow: 'none' },
                              '&.Mui-focusVisible': { outline: 'none', boxShadow: 'none' },
                              textTransform: 'none',
                              fontSize: '1rem',
                              px: { xs: 2, md: 4 },
                              display: { xs: 'block', sm: 'inline-flex' },
                              width: { xs: '100%', sm: 'auto' }
                            }}
                          >
                            {submitting ? 'Starting assessment...' : 'Start Assessment'}
                          </Button>
                        </span>
                      </Tooltip>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'stretch',
                        gap: 1,
                        width: { xs: '100%', sm: 'auto' }
                      }}>
                        {data?.show_go_to_results && (
                          <Button
                            variant="outlined"
                            size="medium"
                            onClick={onGoToResults}
                            fullWidth={true}
                            sx={{
                              color: '#021c2d',
                              borderColor: '#021c2d',
                              backgroundColor: 'white',
                              textTransform: 'none',
                              fontSize: '0.875rem',
                              px: 2,
                              width: { xs: '100%', sm: 'auto' },
                              '&:hover': {
                                backgroundColor: 'rgba(2, 28, 45, 0.04)',
                                borderColor: '#042a44'
                              }
                            }}
                          >
                            Go to Reports
                          </Button>
                        )}
                        <Button
                          variant="text"
                          size="medium"
                          onClick={() => { window.location.href = '/.auth/logout?post_logout_redirect_uri=/'; }}
                          fullWidth={true}
                          sx={{
                            color: '#9ca3af',
                            backgroundColor: 'white',
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            px: 2,
                            width: { xs: '100%', sm: 'auto' },
                            '&:hover': {
                              backgroundColor: 'rgba(156, 163, 175, 0.08)',
                              color: '#6b7280'
                            }
                          }}
                        >
                          Logout
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </>
          </Paper>
        )}
      </Box>
    </Modal>
  );
};

export default WelcomeOverlay;
